import { CourseLevel, CourseTrackType, MediaTypes, SubscriptionPlans } from '@/constants';
import Category from '@/models/category';
import Course, { CoursePhase, Lesson, LessonPart } from '@/models/course';
import { useCourse, useCreateCourse, useDeleteCourse, useUpdateCourse } from '@/services/courses';
import FormModal from '@/shared/components/form-modal';
import DropZoneField from '@/shared/components/form/dropzones/DropZone';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import CheckBoxField from '@/shared/components/form/CheckBox';
import { useEffect, useState } from 'react';
import LessonsEditor from './lessons-editor';
import PhasesEditor from './phases-editor';
import { errorBannerStyle } from '@/shared/dark-inline-styles';

interface CourseModalProps {
    isOpen: boolean;
    selectedCourseId?: string;
    toggle: () => void;
    categories: Category[];
}

const emptyMutationPayload = (): {
    slug: string;
    title: string;
    subtitle: string;
    description: string;
    instructor: string;
    trackType: CourseTrackType;
    level: CourseLevel | '';
    isNew: boolean;
    isFeatured: boolean;
    isFlagship: boolean;
    imagePath: string;
    imageLink: string;
    coverGradient: string;
    categoryId: string;
    subscriptionPlan: SubscriptionPlans;
    orderIndex: number | '';
    phases: CoursePhase[];
    lessons: Lesson[];
} => ({
    slug: '',
    title: '',
    subtitle: '',
    description: '',
    instructor: '',
    trackType: CourseTrackType.General,
    level: '',
    isNew: false,
    isFeatured: false,
    isFlagship: false,
    imagePath: '',
    imageLink: '',
    coverGradient: '',
    categoryId: '',
    subscriptionPlan: SubscriptionPlans.Premium,
    orderIndex: '',
    phases: [],
    lessons: [],
});

// A lesson's parts for validation/payload, tolerant of legacy single-section docs.
const lessonInputParts = (l: Lesson): LessonPart[] => {
    if (l.parts?.length) return l.parts;
    if (l.mediaPath || l.title) {
        return [
            {
                title: l.title ?? '',
                description: l.description ?? '',
                durationSeconds: l.durationSeconds ?? 0,
                mediaPath: l.mediaPath ?? '',
                mediaType: l.mediaType ?? MediaTypes.Audio,
            },
        ];
    }
    return [];
};

const validate = (form: ReturnType<typeof emptyMutationPayload>): string | null => {
    if (!form.slug.trim()) return 'Slug is required';
    if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(form.slug)) return 'Slug must be lowercase letters/numbers/hyphens only';
    if (!form.title.trim()) return 'Title is required';
    if (!form.subtitle.trim()) return 'Subtitle is required';
    if (!form.lessons.length) return 'Course must have at least one lesson';
    // Cover gradient is optional, but if set it must be real CSS gradient syntax —
    // a malformed value renders as no background on the course card.
    if (form.coverGradient.trim() && !/^(repeating-)?(linear|radial|conic)-gradient\(.+\)$/.test(form.coverGradient.trim())) {
        return 'Cover Gradient must be CSS gradient syntax, e.g. linear-gradient(145deg,#F5A623,#EF4444)';
    }
    // Phase accent colours are optional, but if set must be a valid hex colour —
    // a malformed value breaks phase styling on the course page.
    for (const [i, p] of form.phases.entries()) {
        if (p.color?.trim() && !/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(p.color.trim())) {
            return `Phase ${i + 1} accent colour must be a hex colour like #F5A623`;
        }
    }
    for (const [i, l] of form.lessons.entries()) {
        // A lesson has 1–2 parts. Validate every part the admin has started
        // (touched a title/description/media on). Part 2 may be left untouched.
        const parts = lessonInputParts(l);
        const started = parts.filter((p) => p.title?.trim() || p.description?.trim() || p.mediaPath?.trim());
        if (!started.length) return `Lesson ${i + 1} needs at least one part with a title, description and media`;
        for (const [j, p] of started.entries()) {
            const label = started.length > 1 ? `Lesson ${i + 1} part ${j + 1}` : `Lesson ${i + 1}`;
            if (!p.title?.trim()) return `${label} is missing a title`;
            if (!p.description?.trim()) return `${label} is missing a description`;
            if (!p.durationSeconds || p.durationSeconds <= 0) return `${label} needs a positive duration`;
            if (!p.mediaPath?.trim()) return `${label} needs an uploaded audio/video file`;
        }
    }
    return null;
};

const CourseModal = ({ isOpen, toggle, selectedCourseId, categories }: CourseModalProps) => {
    const { data: course, isLoading } = useCourse(selectedCourseId as string, isOpen);
    const { mutateAsync: createCourse, isPending: isCreating } = useCreateCourse();
    const { mutateAsync: updateCourse, isPending: isUpdating } = useUpdateCourse();
    const { mutateAsync: deleteCourse, isPending: isDeleting } = useDeleteCourse();

    const [form, setForm] = useState(emptyMutationPayload());
    const [error, setError] = useState<string | null>(null);
    // Number of media uploads currently in flight (cover image + any lesson
    // part). Save is blocked while > 0 so a course can't be saved with a
    // half-uploaded / missing media reference. Each DropZone reports a balanced
    // true/false (false fires in its `finally`, even on error/unmount).
    const [uploadingCount, setUploadingCount] = useState(0);
    const onUploadingChange = (uploading: boolean) => setUploadingCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    const isUploading = uploadingCount > 0;

    useEffect(() => {
        if (!isOpen) return;
        if (course?.data) {
            const c: Course = course.data;
            setForm({
                slug: c.slug,
                title: c.title,
                subtitle: c.subtitle,
                description: c.description ?? '',
                instructor: c.instructor ?? '',
                trackType: c.trackType,
                level: c.level ?? '',
                isNew: !!c.isNew,
                isFeatured: !!c.isFeatured,
                isFlagship: !!c.isFlagship,
                imagePath: c.imagePath ?? '',
                imageLink: c.imageLink ?? '',
                coverGradient: c.coverGradient ?? '',
                categoryId: c.categoryId ?? '',
                subscriptionPlan: c.subscriptionPlan,
                orderIndex: typeof c.orderIndex === 'number' ? c.orderIndex : '',
                phases: c.phases ?? [],
                lessons: c.lessons ?? [],
            });
        } else if (!selectedCourseId) {
            setForm(emptyMutationPayload());
        }
        setError(null);
        // `isOpen` in the deps so re-opening the SAME course re-fills the form
        // from the (cached) data — otherwise neither dep changes on re-open and
        // the close-reset below leaves it blank.
    }, [isOpen, course?.data, selectedCourseId]);

    useEffect(() => {
        if (!isOpen) {
            setForm(emptyMutationPayload());
            setError(null);
            setUploadingCount(0);
        }
    }, [isOpen]);

    const buildPayload = () => ({
        slug: form.slug.trim(),
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        description: form.description.trim() || undefined,
        instructor: form.instructor.trim() || undefined,
        trackType: form.trackType,
        level: (form.level || undefined) as CourseLevel | undefined,
        isNew: form.isNew,
        isFeatured: form.isFeatured,
        isFlagship: form.isFlagship,
        imagePath: form.imagePath || undefined,
        coverGradient: form.coverGradient.trim() || undefined,
        categoryId: form.categoryId || undefined,
        subscriptionPlan: form.subscriptionPlan,
        orderIndex: form.orderIndex === '' ? undefined : Number(form.orderIndex),
        phases: form.phases.length ? form.phases : undefined,
        lessons: form.lessons.map((l) => {
            // Drop runtime-resolved mediaLink at the lesson level AND from each
            // part — the API rebuilds those on every read. Send only parts the
            // admin actually completed (a part needs media to count).
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { mediaLink, parts, ...rest } = l;
            return {
                ...rest,
                parts: lessonInputParts(l)
                    .filter((p) => p.mediaPath?.trim())
                    .map((p) => ({
                        title: p.title,
                        description: p.description,
                        durationSeconds: p.durationSeconds,
                        mediaPath: p.mediaPath,
                        mediaType: p.mediaType,
                    })),
            };
        }),
    });

    const handleSave = async () => {
        if (isUploading) {
            setError('Please wait — media is still uploading.');
            return;
        }
        const validationError = validate(form);
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            const payload = buildPayload();
            // callApi never throws on API errors — it resolves { status:'error' } —
            // so inspect the result instead of closing as if it saved.
            const result = selectedCourseId
                ? await updateCourse({ courseId: selectedCourseId, ...payload } as Parameters<typeof updateCourse>[0])
                : await createCourse(payload as Parameters<typeof createCourse>[0]);
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to save course.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to save course:', e);
            setError('Failed to save course. Check the console for details.');
        }
    };

    const handleDelete = async () => {
        if (!selectedCourseId) return;
        try {
            const result = await deleteCourse({ courseId: selectedCourseId });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete course.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to delete course:', e);
            setError('Failed to delete course. Check the console for details.');
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!selectedCourseId}
            title={`${selectedCourseId ? 'Edit' : 'Add'} Course`}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && (
                        <div style={errorBannerStyle}>{error}</div>
                    )}
                    <MaterialTextField
                        label="Slug (URL-safe id, e.g. fear-greed)"
                        value={form.slug}
                        onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    />
                    <MaterialTextField label="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                    <MaterialTextField
                        label="Subtitle"
                        value={form.subtitle}
                        onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
                    />
                    <MaterialTextField
                        label="Description (long copy, optional)"
                        multiline
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    />
                    <MaterialTextField
                        label="Instructor (optional)"
                        value={form.instructor}
                        onChange={(e) => setForm((f) => ({ ...f, instructor: e.target.value }))}
                    />
                    <MaterialTextField
                        name="select"
                        select
                        label="Track Type"
                        value={form.trackType}
                        onChange={(e) => setForm((f) => ({ ...f, trackType: e.target.value as CourseTrackType }))}
                    >
                        <MaterialFormOption value={CourseTrackType.Trader}>Trader</MaterialFormOption>
                        <MaterialFormOption value={CourseTrackType.General}>General</MaterialFormOption>
                    </MaterialTextField>
                    <MaterialTextField
                        name="select"
                        select
                        label="Level (optional)"
                        value={form.level}
                        onChange={(e) => setForm((f) => ({ ...f, level: e.target.value as CourseLevel }))}
                    >
                        <MaterialFormOption value="">— None —</MaterialFormOption>
                        <MaterialFormOption value={CourseLevel.Beginner}>Beginner</MaterialFormOption>
                        <MaterialFormOption value={CourseLevel.Intermediate}>Intermediate</MaterialFormOption>
                        <MaterialFormOption value={CourseLevel.Advanced}>Advanced</MaterialFormOption>
                    </MaterialTextField>
                    <MaterialTextField
                        name="select"
                        select
                        label="Category (optional)"
                        value={form.categoryId}
                        onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
                    >
                        <MaterialFormOption value="">— None —</MaterialFormOption>
                        {categories.map((c) => (
                            <MaterialFormOption key={c.id} value={c.id}>
                                {c.name}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>
                    <MaterialTextField
                        name="select"
                        select
                        label="Subscription Plan"
                        value={form.subscriptionPlan}
                        onChange={(e) => setForm((f) => ({ ...f, subscriptionPlan: e.target.value as SubscriptionPlans }))}
                    >
                        <MaterialFormOption value={SubscriptionPlans.Free}>Free</MaterialFormOption>
                        <MaterialFormOption value={SubscriptionPlans.Premium}>Premium</MaterialFormOption>
                    </MaterialTextField>
                    <MaterialTextField
                        label="Order Index (lower shows first)"
                        type="number"
                        value={form.orderIndex}
                        onChange={(e) => setForm((f) => ({ ...f, orderIndex: e.target.value === '' ? '' : Number(e.target.value) }))}
                    />
                    <div style={{ display: 'flex', gap: 16, marginTop: 12, marginBottom: 12 }}>
                        <CheckBoxField
                            name="isNew"
                            checked={form.isNew}
                            onChange={() => setForm((f) => ({ ...f, isNew: !f.isNew }))}
                            label="New badge"
                        />
                        <CheckBoxField
                            name="isFeatured"
                            checked={form.isFeatured}
                            onChange={() => setForm((f) => ({ ...f, isFeatured: !f.isFeatured }))}
                            label="Featured"
                        />
                        <CheckBoxField
                            name="isFlagship"
                            checked={form.isFlagship}
                            onChange={() => setForm((f) => ({ ...f, isFlagship: !f.isFlagship }))}
                            label="Flagship (renders on a dedicated marketing page)"
                        />
                    </div>
                    <MaterialTextField
                        label="Cover Gradient CSS (fallback if no image, e.g. linear-gradient(145deg,#F5A623,#EF4444))"
                        value={form.coverGradient}
                        onChange={(e) => setForm((f) => ({ ...f, coverGradient: e.target.value }))}
                    />
                    <div style={{ marginTop: 16 }}>
                        <p style={{ marginBottom: 6 }}>Cover Image (optional — falls back to gradient)</p>
                        <DropZoneField
                            customHeight
                            accept="image/jpeg, image/jpg, image/png"
                            currentValue={form.imageLink || form.imagePath || ''}
                            currentFileType="image"
                            onChange={(imagePath) => setForm((f) => ({ ...f, imagePath }))}
                            onUploadingChange={onUploadingChange}
                        />
                    </div>
                    <div style={{ marginTop: 20 }}>
                        <PhasesEditor
                            phases={form.phases}
                            onChange={(nextPhases) =>
                                setForm((f) => {
                                    // Sweep lesson.phaseId references that no longer point at an existing
                                    // phase — otherwise saving leaves orphan refs that the app/site have
                                    // to handle defensively forever after.
                                    const liveIds = new Set(nextPhases.map((p) => p.id));
                                    const sweptLessons = f.lessons.map((l) =>
                                        l.phaseId && !liveIds.has(l.phaseId) ? { ...l, phaseId: undefined } : l,
                                    );
                                    return { ...f, phases: nextPhases, lessons: sweptLessons };
                                })
                            }
                        />
                    </div>
                    <div style={{ marginTop: 20 }}>
                        <LessonsEditor
                            lessons={form.lessons}
                            phases={form.phases}
                            onChange={(updater) => setForm((f) => ({ ...f, lessons: updater(f.lessons) }))}
                            onUploadingChange={onUploadingChange}
                        />
                    </div>
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isCreating || isUpdating || isDeleting}
            saveDisabled={isUploading}
            saveDisabledHint="Uploading media… please wait until it finishes before saving."
        />
    );
};

export default CourseModal;
